import { apiClient } from '../client';

export interface MCPServer {
  id: string;
  name: string;
  description: string;
  type: 'external' | 'npx' | 'python' | 'golang';
  status: 'inactive' | 'starting' | 'active' | 'stopping' | 'error' | 'unhealthy';
  url?: string;
  transport: 'stdio' | 'http' | 'sse' | 'websocket';
  command?: string;
  args?: string[];
  code_path?: string;
  env_vars?: Record<string, string>;
  working_dir?: string;
  process_id?: number;
  port?: number;
  auto_restart: boolean;
  auth_required?: boolean;
  auth_type?: string;
  webhook_url?: string;
  webhook_secret?: string;
  webhook_events?: string[];
  version: string;
  tags?: string[];
  metadata?: Record<string, any>;
  last_sync: string;
  created_at: string;
  updated_at: string;
}

export interface ServerCapabilities {
  tools: ToolInfo[];
  resources: ResourceInfo[];
  prompts: PromptInfo[];
}

export interface ToolInfo {
  name: string;
  description: string;
  input_schema: Record<string, any>;
}

export interface ResourceInfo {
  uri: string;
  name: string;
  description?: string;
  mime_type?: string;
}

export interface PromptInfo {
  name: string;
  description?: string;
  arguments?: PromptArgumentInfo[];
}

export interface PromptArgumentInfo {
  name: string;
  description?: string;
  required: boolean;
}

export interface ServerStats {
  total_requests: number;
  failed_requests: number;
  average_latency: number;
  last_request_time: string;
  uptime: number;
}

export interface ServerLogEntry {
  timestamp: string;
  level: string;
  message: string;
  data?: Record<string, any>;
}

export interface ToolCallRequest {
  server_id: string;
  tool_name: string;
  args: Record<string, any>;
}

export interface ToolCallResponse {
  content: ContentItem[];
  is_error: boolean;
}

export interface ContentItem {
  type: string;
  text?: string;
  data?: string;
  mime_type?: string;
  metadata?: Record<string, any>;
}

export interface ResourceReadRequest {
  server_id: string;
  uri: string;
}

export interface ResourceReadResponse {
  contents: ResourceContent[];
}

export interface ResourceContent {
  uri: string;
  mime_type?: string;
  text?: string;
  blob?: string;
}

export interface PromptGetRequest {
  server_id: string;
  name: string;
  arguments?: Record<string, any>;
}

export interface PromptGetResponse {
  description?: string;
  messages: PromptMessage[];
}

export interface PromptMessage {
  role: string;
  content: ContentItem;
}

export const mcpService = {
  // Server management
  createServer: (data: Partial<MCPServer>) =>
    apiClient.post<MCPServer>('/api/mcp/servers', data),

  listServers: () =>
    apiClient.get<{ servers: MCPServer[]; count: number }>('/api/mcp/servers'),

  getServer: (serverId: string) =>
    apiClient.get<MCPServer>(`/api/mcp/servers/${serverId}`),

  updateServer: (serverId: string, data: Partial<MCPServer>) =>
    apiClient.put<MCPServer>(`/api/mcp/servers/${serverId}`, data),

  deleteServer: (serverId: string) =>
    apiClient.delete(`/api/mcp/servers/${serverId}`),

  // Server control
  startServer: (serverId: string) =>
    apiClient.post(`/api/mcp/servers/${serverId}/start`),

  stopServer: (serverId: string) =>
    apiClient.post(`/api/mcp/servers/${serverId}/stop`),

  restartServer: (serverId: string) =>
    apiClient.post(`/api/mcp/servers/${serverId}/restart`),

  // Server information
  getCapabilities: (serverId: string) =>
    apiClient.get<ServerCapabilities>(`/api/mcp/servers/${serverId}/capabilities`),

  getLogs: (serverId: string, limit?: number) =>
    apiClient.get<{ logs: ServerLogEntry[]; count: number }>(
      `/api/mcp/servers/${serverId}/logs`,
      { params: { limit } }
    ),

  getStats: (serverId: string) =>
    apiClient.get<ServerStats>(`/api/mcp/servers/${serverId}/stats`),

  // MCP operations
  callTool: (data: ToolCallRequest) =>
    apiClient.post<ToolCallResponse>('/api/mcp/tools/call', data),

  readResource: (data: ResourceReadRequest) =>
    apiClient.post<ResourceReadResponse>('/api/mcp/resources/read', data),

  getPrompt: (data: PromptGetRequest) =>
    apiClient.post<PromptGetResponse>('/api/mcp/prompts/get', data),

  // OAuth management
  getOAuthStatus: (serverId: string) =>
    apiClient.get<OAuthStatus>(`/api/mcp/servers/${serverId}/oauth/status`),

  updateOAuthCredentials: (serverId: string, credentials: any) =>
    apiClient.put(`/api/mcp/servers/${serverId}/oauth`, credentials),

  // OAuth flow
  initiateOAuthFlow: (serverId: string, organizationId?: string) =>
    apiClient.post<OAuthInitiateResponse>(`/api/mcp/servers/${serverId}/oauth/initiate`, {
      organization_id: organizationId,
    }),

  refreshOAuthToken: (serverId: string) =>
    apiClient.post(`/api/mcp/servers/${serverId}/oauth/refresh`),

  // Deep linking
  parseDeepLink: (deepLink: string) =>
    apiClient.post<DeepLinkParseResponse>('/api/mcp/deeplink/parse', { deep_link: deepLink }),

  createServerFromDeepLink: (deepLink: string) =>
    apiClient.post<MCPServer>('/api/mcp/deeplink/create', { deep_link: deepLink }),

  generateDeepLink: (serverId: string, clientScheme?: string) =>
    apiClient.post<DeepLinkGenerateResponse>('/api/mcp/deeplink/generate', {
      server_id: serverId,
      client_scheme: clientScheme,
    }),
};

// OAuth Status interface
export interface OAuthStatus {
  is_authenticated: boolean;
  expires_at?: string;
  requires_reauth: boolean;
  last_auth_time?: string;
}

// OAuth Flow interfaces
export interface OAuthInitiateResponse {
  authorization_url: string;
  state: string;
  requires_auth: boolean;
}

// Deep Link interfaces
export interface DeepLinkParseResponse {
  name: string;
  config: Partial<MCPServer>;
}

export interface DeepLinkGenerateResponse {
  deep_link: string;
  name: string;
  config: any;
}
